import React, {
  lazy,
  Suspense,
  useEffect,
  useId,
  useRef,
  useState,
  useCallback,
  type ReactElement,
} from "react";
import {getSrc} from "gatsby-plugin-image";
import {deepMergeObject} from "/src/utils/utils";

import Icon from "../../icon/Icon";
import Image, {ImageObject} from "../../image/Image";
import TransitionBlock, {
  TRANSITION_BLOCK_FROM,
} from "../../animation/transition-block/TransitionBlock";
import MarkdownText from "../../markdown/markdown-text/MarkdownText";

import useHasWindow from "/src/hooks/useHasWindow";
import DefaultPlayerIcon from "/src/svg/video-player-icon.svg";
import translate from "/src/utils/translate";
import {css} from "/src/utils/utils";
import KEY from "/src/constants/keyboard-keydown-key";

import * as styles from "./video.module.scss";
import type {VideoProps as BaseVideoProps} from "./Video";
import {VideoType} from "./Video";

// Extended VideoProps
interface VideoOptimizedProps extends BaseVideoProps {
  useNativeVideo?: boolean; // Use native video element instead of ReactPlayer (better for LCP)
  posterImage?: string; // Custom poster image URL for native video element
}

export type VideoProps = VideoOptimizedProps;

// Lazy load heavy dependencies
const ReactPlayer = lazy(() => import("react-player/lazy"));
const Modal = lazy(() => import("../../modal/Modal"));
const ErrorBoundary = lazy(() => import("../../error-boundary/ErrorBoundary"));

// SVG placeholder for videos without poster
const createPlaceholderSVG = (
  aspectRatio: string = "16/9",
  color: string = "#f7f7f8",
  opacity: number = 0.3
) => {
  const [width, height] = aspectRatio.split("/").map(Number);
  const viewBox = `0 0 ${width * 100} ${height * 100}`;
  const encodedColor = encodeURIComponent(color);
  return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='${viewBox}'%3E%3Crect width='100%25' height='100%25' fill='${encodedColor}' opacity='${opacity}'/%3E%3C/svg%3E`;
};

// Simple Video Facade Component
const VideoFacade = ({
  thumbnail,
  thumbnailImage,
  showPlayButton,
  onClick,
  className,
  customPlayIcon,
  iconClasses,
  aspectRatio = "16/9",
}: {
  thumbnail?: string | null;
  thumbnailImage?: ImageObject | null;
  showPlayButton: boolean;
  onClick?: () => void;
  className?: string;
  customPlayIcon?: any;
  iconClasses?: string;
  aspectRatio?: string;
}) => {
  return (
    <div
      className={className}
      onClick={onClick}
      style={{
        cursor: onClick ? "pointer" : "default",
        display: "grid",
        width: "100%",
        height: "100%",
      }}
    >
      {thumbnailImage?.gatsbyImageData ? (
        <div
          style={{gridArea: "1 / 1", width: "100%", height: "100%", zIndex: 0}}
        >
          <Image image={thumbnailImage} objectFit="cover" loading="lazy" />
        </div>
      ) : (
        // Fallback to SVG placeholder or custom poster URL
        <img
          src={thumbnail || createPlaceholderSVG(aspectRatio)}
          alt=""
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            gridArea: "1 / 1",
            zIndex: 0,
          }}
        />
      )}
      {showPlayButton && (
        <div
          style={{
            gridArea: "1 / 1",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
            zIndex: 1,
          }}
        >
          <Icon
            role="presentation"
            aria-label={translate("Play Video")}
            classes={css(styles.video__icon, iconClasses || "")}
            svg={customPlayIcon || DefaultPlayerIcon}
          />
        </div>
      )}
    </div>
  );
};

// Optimized Video Component - Clean and Simple
const VideoOptimizedClean = (props: VideoProps): ReactElement | null => {
  const {
    captionHtmlSanitized,
    captionClasses,
    customPlayIcon,
    iconClasses,
    showControl = true,
    type = "autoplay" as VideoType,
    videoClasses,
    videoConfig = {},
    videoSource,
    videoThumbnail,
    videoTitle = translate("Video Player"),
    videoRef,
    playing,
    portrait = false,
    hasAudio = true,
    useNativeVideo = false,
    posterImage,
  } = props;

  // Core state
  const baseElemId = useId();
  const [showModal, setShowModal] = useState(false);
  const [isPlayerLoaded, setIsPlayerLoaded] = useState(useNativeVideo);
  const [userInitiatedPlay, setUserInitiatedPlay] = useState(false);
  const facadeRef = useRef<HTMLDivElement | null>(null);
  const hasWindow = useHasWindow();

  // Determine playing state and thumbnail
  // Priority: user click > playing prop > type-based default
  const playingState =
    userInitiatedPlay || // User clicked, always play
    (playing !== undefined ? playing : type === "autoplay"); // Otherwise use prop or autoplay
  const thumbnailSource =
    posterImage ||
    (videoThumbnail?.gatsbyImageData
      ? getSrc(videoThumbnail.gatsbyImageData)
      : null);

  // Get aspect ratio for placeholder
  const aspectRatio = portrait ? "9/16" : "16/9";

  // Lazy load video player on viewport intersection
  useEffect(() => {
    // Popup and clicktoplay videos don't need intersection observer
    // Also skip if we're using native video
    if (
      type === "popup" ||
      type === "clicktoplay" ||
      !hasWindow ||
      isPlayerLoaded ||
      useNativeVideo
    )
      return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsPlayerLoaded(true);
        }
      },
      // Load earlier for autoplay to ensure smooth playback
      {rootMargin: type === "autoplay" ? "100px" : "50px"}
    );

    if (facadeRef.current) {
      observer.observe(facadeRef.current);
      return () => observer.disconnect();
    }
  }, [type, hasWindow, isPlayerLoaded, useNativeVideo]);

  // Keyboard handler for popup videos
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if ((e.key === KEY.ENTER || e.key === KEY.SPACE) && type === "popup") {
        e.preventDefault();
        setShowModal(true);
      }
    },
    [type]
  );

  // Early return for missing requirements
  if (!videoSource) {
    return null;
  }

  // Use native video element for better LCP (renders during SSG/SSR)
  if (useNativeVideo) {
    return (
      <video
        className={css(styles.video__player, videoClasses || "")}
        poster={thumbnailSource || createPlaceholderSVG(aspectRatio)}
        autoPlay={type === "autoplay"}
        loop={type === "autoplay"}
        muted={type === "autoplay" || !hasAudio}
        playsInline
        controls={showControl && type !== "autoplay"}
        style={{width: "100%", height: "100%", objectFit: "cover"}}
      >
        <source src={videoSource} type="video/mp4" />
      </video>
    );
  }

  // Client-side only check for ReactPlayer
  if (!hasWindow) {
    return null;
  }

  // Build player config based on type
  const getPlayerConfig = () => {
    const baseConfig: any = {
      attributes: {
        playsInline: true,
        poster: thumbnailSource || createPlaceholderSVG(aspectRatio), // Add poster attribute
      },
      wistia: {
        options: {
          transparentLetterbox: true,
          playBarControl: showControl,
          playPauseControl: showControl,
          settingsControl: showControl,
          qualityControl: showControl,
          playbackRateControl: showControl,
          volumeControl: showControl,
          fullscreenControl: showControl,
          volume: 0.5,
          aspect: portrait ? "9 / 16" : "16 / 9",
          ...(thumbnailSource ? {poster: thumbnailSource} : {}),
        },
      },
    };

    // Add type-specific config
    if (type === "autoplay") {
      baseConfig.wistia.options = {
        ...baseConfig.wistia.options,
        autoplay: true,
        silentAutoplay: "allow",
        muted: true,
        endVideoBehavior: "loop",
        controlsVisibleOnLoad: false,
        bigPlayButton: false,
      };
    } else if (type === "inline") {
      baseConfig.wistia.options = {
        ...baseConfig.wistia.options,
        wmode: "transparent",
      };
    }

    return deepMergeObject(baseConfig, videoConfig);
  };

  // Render video based on type
  const renderVideo = () => {
    const playerConfig = getPlayerConfig();

    switch (type) {
      case "popup":
        return (
          <>
            <button
              aria-label={translate("Play Video")}
              className={css(styles.video__button)}
              onKeyDown={handleKeyDown}
              onClick={() => setShowModal(true)}
              type="button"
            >
              <div
                className={css(styles.video__thumbnail, videoClasses || "")}
                style={{display: "grid", width: "100%", height: "100%"}}
              >
                {videoThumbnail?.gatsbyImageData ? (
                  <div
                    style={{
                      gridArea: "1 / 1",
                      width: "100%",
                      height: "100%",
                      zIndex: 0,
                    }}
                  >
                    <Image
                      image={videoThumbnail}
                      objectFit="cover"
                      loading="lazy"
                    />
                  </div>
                ) : thumbnailSource ? (
                  <img
                    src={thumbnailSource}
                    alt=""
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      gridArea: "1 / 1",
                      zIndex: 0,
                    }}
                  />
                ) : null}
                <div
                  style={{
                    gridArea: "1 / 1",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    pointerEvents: "none",
                    zIndex: 1,
                  }}
                >
                  <Icon
                    role="presentation"
                    aria-label={translate("Play Video")}
                    classes={css(styles.video__icon, iconClasses || "")}
                    svg={customPlayIcon || DefaultPlayerIcon}
                  />
                </div>
              </div>
            </button>
            {showModal && (
              <Suspense fallback={null}>
                <Modal
                  portrait={portrait}
                  ariaLabelledBy={`${baseElemId}-title`}
                  onClose={() => setShowModal(false)}
                >
                  <div
                    className={css(
                      styles.video,
                      portrait ? styles.videoPortrait : ""
                    )}
                  >
                    <h3 id={`${baseElemId}-title`} hidden>
                      {videoTitle}
                    </h3>
                    <ReactPlayer
                      ref={videoRef}
                      url={videoSource}
                      className={css(
                        styles.video__player,
                        portrait ? styles.video__playerPortrait : "",
                        videoClasses || ""
                      )}
                      config={playerConfig}
                      controls={showControl}
                      playing={showModal}
                      height="100%"
                      width="100%"
                    />
                  </div>
                </Modal>
              </Suspense>
            )}
          </>
        );

      case "inline":
        if (!isPlayerLoaded) {
          return (
            <div ref={facadeRef}>
              <VideoFacade
                thumbnail={thumbnailSource}
                thumbnailImage={videoThumbnail}
                showPlayButton={true}
                onClick={() => setIsPlayerLoaded(true)}
                className={css(
                  styles.video__player,
                  styles.video__playerInline,
                  styles.video__playerFacade,
                  videoClasses || ""
                )}
                customPlayIcon={customPlayIcon}
                iconClasses={iconClasses}
                aspectRatio={aspectRatio}
              />
            </div>
          );
        }
        return (
          <Suspense
            fallback={
              <div
                className={css(
                  styles.video__player,
                  styles.video__playerInline,
                  videoClasses || ""
                )}
              />
            }
          >
            <ReactPlayer
              ref={videoRef}
              className={css(
                styles.video__player,
                styles.video__playerInline,
                videoClasses || ""
              )}
              url={videoSource}
              controls={showControl}
              width="100%"
              height="100%"
              playing={playingState}
              config={playerConfig}
              muted={!hasAudio}
            />
          </Suspense>
        );

      default: // autoplay and clicktoplay
        if (!isPlayerLoaded) {
          return (
            <div ref={facadeRef}>
              <VideoFacade
                thumbnail={thumbnailSource}
                thumbnailImage={videoThumbnail}
                showPlayButton={type !== "autoplay"} // No button for autoplay
                onClick={
                  type !== "autoplay"
                    ? () => {
                        setIsPlayerLoaded(true);
                        setUserInitiatedPlay(true);
                      }
                    : undefined
                }
                className={css(styles.video__player, videoClasses || "")}
                customPlayIcon={customPlayIcon}
                iconClasses={iconClasses}
                aspectRatio={aspectRatio}
              />
            </div>
          );
        }
        return (
          <Suspense
            fallback={
              <div className={css(styles.video__player, videoClasses || "")} />
            }
          >
            <ErrorBoundary source="video">
              <ReactPlayer
                ref={videoRef}
                className={css(styles.video__player, videoClasses || "")}
                url={videoSource}
                config={playerConfig}
                width="100%"
                height="100%"
                playing={playingState}
                muted={type === "autoplay" || !hasAudio}
              />
            </ErrorBoundary>
          </Suspense>
        );
    }
  };

  // Caption content
  const captionContent = captionHtmlSanitized ? (
    <div className={css(styles.video__caption, captionClasses || "")}>
      <TransitionBlock
        triggerOnce
        from={TRANSITION_BLOCK_FROM.bottom}
        delay={150}
        transition="cubic-bezier(0.22, 0.61, 0.36, 1)"
      >
        <MarkdownText htmlContent={captionHtmlSanitized} />
      </TransitionBlock>
    </div>
  ) : null;

  // Return structure matching original
  const videoElement = renderVideo();

  if (!captionHtmlSanitized) {
    return videoElement;
  }

  return (
    <div className={styles.video__captionWrapper}>
      <div className={styles.video}>{videoElement}</div>
      {captionContent}
    </div>
  );
};

export default VideoOptimizedClean;
export {VideoType};
